<!-- @format -->
<script lang="ts">
  import { getStoresContext } from "~/stores";
  import type { StoresKey } from "~/stores";
  import { getFobs } from "../../configuration";
  import { slide } from "svelte/transition";
  import { createEventDispatcher } from "svelte";

  export let request_store_key: StoresKey;
  export const TOGGLE_ALL = 'toggle_all';
  export const ALL_FLOORS = 'all_floor';
  export let btn_selected:any;
  export let is_fob_page = false;
  export let lineOfBusinessID: number = 0;

  const stores_context = getStoresContext(request_store_key);
  const { T, reverse_router, scdata } = stores_context;
  const dispatch = createEventDispatcher();  

  $: t = $T;
  $: innerWidth = 0;
  $: isOpen = innerWidth > 768 || false;

  // On pages with lineOfBusinessID, we're already in a specific category
  // so we should only be showing floor filters
  $: isOnCategoryPage = !!lineOfBusinessID;

  let search_term = '';
  let checkedFOBs:any = isOnCategoryPage ? [] : [TOGGLE_ALL];
  let checkedFloors = [ALL_FLOORS];

  $: stores_path = $reverse_router.routePurpose("stores collection");
  // $: floormap_path = $reverse_router.routePurpose('floor map');

  const changeCategory = (href: string) => () => {
    window.location.href = href;
  }
  const toggle_buttons = $scdata ? [
      { id: TOGGLE_ALL, text: $T`fob-all`, type: 'fob' },
      ...(getFobs($scdata) || []).map((fob) => ({
        id: fob.id,
        text: fob.description,
        type: 'fob'
      }))
    ] : [];

  $: floors = [
    { description: t`all-floors`, id: ALL_FLOORS, order: 0, type: 'floor' },
    ...($scdata?.floors || []).map(floor => ({
      ...floor, 
      type: 'floor',
      // Ensure floor.id is stored as a string for consistent comparison
      id: typeof floor.id === 'number' ? String(floor.id) : floor.id
    }))
  ].sort((a, b) => (a.order > b.order ? 1 : b.order > a.order ? -1 : 0));

  function onChangeFOB(e: Event) {
    const target = e.target as HTMLInputElement;
    const checked = target.checked;
    if (target.value != TOGGLE_ALL && checked == true) {
      checkedFOBs = checkedFOBs.filter((i: any) => i != TOGGLE_ALL);
    } else if (target.value == TOGGLE_ALL && checked == true) {
      checkedFOBs = [TOGGLE_ALL];
    } else if (target.value == TOGGLE_ALL && checked == false) {
      checkedFOBs = [];
    }

    if (checkedFOBs.length == 0) {
      checkedFOBs = [TOGGLE_ALL];
    }

    btn_selected = combineFloorsAndFOBs(checkedFOBs, checkedFloors);
    dispatch('select', btn_selected );
  }

  function combineFloorsAndFOBs(checkedFOBs:any[], checkedFloors:string[]) {
    var y = toggle_buttons?.filter((i) => checkedFOBs.includes(i.id)) || [];
    var x = floors?.filter((i) => {
      // Convert floor id to string for comparison if necessary
      const floorId = typeof i.id === 'number' ? String(i.id) : i.id;
      return checkedFloors.includes(floorId);
    }) || [];
    
    // Convert floor objects to have the same structure as FOB objects
    const floorObjects = x.map(floor => ({
      id: floor.id,
      text: floor.description,
      type: 'floor'
    }));
    
    return [...y, ...floorObjects];
  }

  // Initialize button selection with lineOfBusinessID if provided
  $: if (lineOfBusinessID && $scdata) {
    // For category pages, we don't need to select any FOB
    if (isOnCategoryPage) {
      checkedFOBs = [];
      btn_selected = floors
        .filter(floor => checkedFloors.includes(String(floor.id)))
        .map(floor => ({
          id: floor.id,
          text: floor.description,
          type: 'floor'
        }));
    } else {
      // Find which FOB corresponds to this lineOfBusinessID
      const matchingFob = toggle_buttons?.find(btn => 
        btn.id === lineOfBusinessID || 
        // Handle the case when we're in a page with a FOB category pre-selected
        (btn.type === 'fob' && btn.id !== TOGGLE_ALL)
      );
      
      if (matchingFob) {
        checkedFOBs = [matchingFob.id];
        btn_selected = combineFloorsAndFOBs(checkedFOBs, checkedFloors);
      }
    }
  }

  function onChangeFloor(e: Event) {
    const target = e.target as HTMLInputElement;
    const checked = target.checked;
    
    if (target.value != ALL_FLOORS && checked == true) {
      checkedFloors = checkedFloors.filter((i: string) => i != ALL_FLOORS);
    } else if (target.value == ALL_FLOORS && checked == true) {
      checkedFloors = [ALL_FLOORS];
    } else if (target.value == ALL_FLOORS && checked == false) {
      checkedFloors = [];
    }

    if (checkedFloors.length == 0) {
      checkedFloors = [ALL_FLOORS];
    }

    // On category pages, we only pass floor filters
    if (isOnCategoryPage) {
      btn_selected = floors
        .filter(floor => {
          const floorId = typeof floor.id === 'number' ? String(floor.id) : floor.id;
          const isIncluded = checkedFloors.includes(floorId);
          return isIncluded;
        })
        .map(floor => ({
          id: floor.id,
          text: floor.description,
          type: 'floor'
        }));
    } else {
      btn_selected = combineFloorsAndFOBs(checkedFOBs, checkedFloors);
    }

    dispatch('select', btn_selected);
  }

  const search = () => {
    dispatch('search', search_term);
  };

</script>

<svelte:window bind:innerWidth />
<div class="toggle-btn-group col-12">
  <div class="form-group p-lg-3 p-auto">
    <button 
      class="me-2 justify-content-between purple-button fw-normal"
      on:click={() => (isOpen = !isOpen)}
      on:keydown={() => (isOpen = !isOpen)}
    >
      {t`filter-and-organize`}
      <i class={ isOpen? 'bi bi-chevron-down' : 'bi bi-chevron-right'} />
    </button>
    <br />
    {#if isOpen}
      <div transition:slide|local>
        <!-- Collapse filters -->
          <div class="input-group">
            <input
              bind:value={search_term}
              on:change={search}
              on:input={search}
              type="text"
              class="form-control"
              placeholder={t`write-a-search-word`}
              aria-label="haku-sana"
              aria-describedby="basic-addon2"
            />
            <div class="input-group-append">
              <button on:click={search} class="purple-button" type="button"
                ><i class="bi bi-search" /></button
              >
            </div>
          </div>
          <br />
          {#if $scdata && !is_fob_page && !isOnCategoryPage}
            <div class="mb-3">
              <h5 class="filter-title">{t`categories`}</h5>
              {#each toggle_buttons as btn}
                {#if !lineOfBusinessID || btn.id === TOGGLE_ALL || btn.id === lineOfBusinessID}
                  <label>
                    <input
                      type="checkbox"
                      bind:group={checkedFOBs}
                      name="category"
                      value={btn.id}
                      on:change={onChangeFOB}
                    /> 
                    <span class="col-2 icon">
                      <i class="bi bi-check-lg" class:active={btn_selected?.includes(String(btn.id))} />
                    </span>
                    <span class="col-10">{btn.text}</span>
                    
                  </label>
                {/if}
              {/each}
            </div>
          {/if}
          <div class="mb-3">
            <h5 class="filter-title">{t`floor`}</h5>
            {#each floors as btn}
              <label>
                <input
                type="checkbox"
                bind:group={checkedFloors}
                name="category"
                value={btn.id}
                on:change={onChangeFloor}
              />
                <span class="col-2 icon">
                  <i class="bi bi-check-lg" class:active={btn_selected?.includes(String(btn.id))} />
                </span>
                <!-- <span class="col-10">{t( btn.description) ||  btn.description}</span> -->
                <span class="col-10">{  btn.description}</span>
              </label>
            {/each}
          </div>
      <!-- Collapse filters -->
      </div>
    {/if}
  </div>

  <div class="form-group mb-3 p-lg-0">
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <div on:click={changeCategory(`${stores_path}?display=alphabetical-order`)}
      class="purple-button w-100 text-start d-block fw-normal"
    >
      <i class="bi bi-chevron-right mr-2" />
      {t`alphabetical-order`}
    </div>
  </div>
  <div class="form-group mb-3 p-lg-0">
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <div on:click={changeCategory(`${stores_path}?display=opening-hours`)}
      class="purple-button w-100 text-start d-block fw-normal"
    >
      <i class="bi bi-chevron-right mr-2" />
      {t`business-opening-hours`}
    </div>
  </div>
  <div class="form-group mb-3 p-lg-0">
    <a
      href="https://www.wayfinding.fi/tripla-web/"
      target="_blank"
      rel="noreferrer"
      class="purple-button w-100 text-start d-block fw-normal"
    >
      <i class="bi bi-chevron-right mr-2" />
      {t`floor-map`}
    </a>
  </div>
</div>

<style lang="scss">
  .filter-title {
    font-family: var(--font-main);
    font-weight: 500;
    font-size: 20px;
  }
  .bi {
    font-weight: bold !important;
    font-size: 1.2rem;
  }

  .toggle-btn-group {
    @media screen and (min-width: 992px) {
      padding: 20px;
      background: var(--color-accent-secondary-light);
    }
    h5 {
      margin-bottom: 1.5rem;
    }

    label {
      display: flex;
      margin-bottom: 6px;
      align-items: center;
      cursor: pointer;
      font-weight: normal;
      font-size: 16px;
      line-height: 24px;

    }

    label input {
      display: none; /* Hide the default checkbox */
    }

    /* Style the artificial checkbox */
    label span.icon {
      height: 38px;
      width: 38px;
      border: 0 solid grey;
      display: inline-block;
      position: relative;
      background-color: var(--color-accent-secondary-light);
      cursor: pointer;
      margin-right: 15px;
    }

    .form-group {
      background-color: #fff;
      margin-bottom: 30px;

      @media (max-width: 991px) {
        margin-bottom: 20px;
      }
    }

    input.form-control {
      border-radius: 0;
      background-color: var(--color-accent-secondary-light);
      border-color: transparent;
    }
    input.form-control:focus {
      color: #212529;
      background-color: '';
      border-color: '';
      outline: 0;
      box-shadow: none;
    }

    .me-2 {
      display: flex;
      align-items: center;
      width: 100%;
      i {
        font-size: 1.2rem;
      }
    }

    .bi-check-lg {
      position: absolute;
      font-size: 38px;
      top: -10px;
      color: var(--color-accent-primary-text);
      display: none;
    }
    .active {
      display: block;
    }

    /* Style its checked state...with a ticked icon */
    [type='checkbox']:checked + span .bi-check-lg {
      display: block;
    }

    .purple-button:hover {
      cursor: pointer;
    }
  }
</style>
